import styled from 'styled-components';
import React, { useState } from "react";
import * as d3 from 'd3';

import ChartDimensions from '../../models/ChartDimensions';
import ChartMargins from '../../models/ChartMargins';
import ChartPaddings from '../../models/ChartPaddings';
import HorizontalAxis from './HorizontalAxis';
import Scalable from '../../interfaces/Scalable';
import VerticalAxis from './VerticalAxis';
import classNames from 'classnames';


const DIMENSIONS: ChartDimensions = {
  width: 720,
  height: 360,
}

const MARGINS: ChartMargins = {
  top: 15,
  left: 70,
  bottom: 65,
  right: 10,
};

const PADDINGS: ChartPaddings = {
  left: 30,
  right: 30,
};

const POINT_RADIUS = 10;

// TODO: light style for scatter plot
const StyledScatterPlot = styled.svg`
  & circle {
    stroke-width: 4px;
    stroke: #CC0000;
  }

  & text {
    font-size: 13px;
    font-family: MapfreText-Light;
    pointer-events: none;

    &.label-point {
      font-family: MapfreText-Bold;
    }
  }

  & .entry-item {
    fill-opacity: 1;
    stroke-opacity: 1;

    transition: fill-opacity 0.2s ease-out, stroke-opacity 0.2s ease-out;
  }

  & .entry-item.unfocused {
    fill-opacity: 0.1;
    stroke-opacity: 0.1;
  }

  & .trendline {
    stroke-width: 2px;
    stroke-linecap: round;
    stroke-linejoin: miter;
    stroke-dasharray: 5px 5px;
    fill: none;

    transition: stroke-opacity 0.2s ease-out;
  }

  & .trendline.unfocused {
    stroke-opacity: 0.1;
  }

  &.dark {
    stroke: #464646;

    circle {
      fill: #464646;
    }

    text {
      stroke: none;
      fill: #464646;
    }

    & .trendline {
      stroke: #464646;
    }
  }

  &.light {
    stroke: #FBFBFB;

    circle {
      fill: #FBFBFB;
    }

    text {
      stroke: none;
      fill: #FBFBFB;
    }

    & .trendline {
      stroke: white;
    }
  }
`;

export interface DataPoint {
  x: number,
  y: number,
  trend?: number,
  label: string,
}

export interface TrendlinePoint {
  x: number,
  y: number,
}

interface ScatterPlotProps {
  points: DataPoint[],
  isDark: boolean,
  formatVerticalTicks?: (n: number) => string,
  xExtent?: [number, number],
  yExtent?: [number, number]
}

interface PointsProps extends Scalable {
  points: DataPoint[],
  selectedPoints: Set<string>,
  setSelectedPoints: React.Dispatch<React.SetStateAction<Set<string>>>,
}

interface TrendlineProps extends Scalable {
  points: DataPoint[],
  selectedPoints: Set<string>
}

const Points: React.FC<PointsProps> = (props: PointsProps) => {
  const onMouseEnter = (label: string) => {
    return () => {
      props.selectedPoints.add(label);
      props.setSelectedPoints(new Set(props.selectedPoints));
    }
  };

  const onMouseLeave = (label: string) => {
    return () => {
      props.selectedPoints.delete(label);
      props.setSelectedPoints(new Set(props.selectedPoints));
    }
  };

  return (
    <g>
      { props.points.map(point => {
          return (
            <g
              className={classNames('entry-item', { 'unfocused': props.selectedPoints.size > 0 && !props.selectedPoints.has(point.label) })}
              key={ `fragment-${point.label}` }
            >
              <circle
                  key={ `circle-${point.label}` }
                  cx={ props.xScale(point.x) }
                  cy={ props.yScale(point.y)}
                  r={ POINT_RADIUS }
                  className='circle-point'
                  onMouseEnter={ onMouseEnter(point.label) }
                  onMouseLeave={ onMouseLeave(point.label) }
                />
              <text
                  key={ `text-${point.label}` }
                  x={ props.xScale(point.x) + POINT_RADIUS + 10 }
                  y={ props.yScale(point.y) }
                  className='label-point'
                >
                { point.label }
              </text>
            </g>
          );
        })
      }
    </g>
  )
};

const Trendline = (props: TrendlineProps) => {
  const line = d3.line()
    // @ts-ignore
    .x((p: DataPoint) => props.xScale(p.x))
    // @ts-ignore
    .y((p: DataPoint) => props.yScale(p.trend))
    .curve(d3.curveCatmullRom);

  const points = props
    .points
    .filter((p: DataPoint) => p.trend !== null )
    .sort((p1: DataPoint, p2: DataPoint) => p1.x - p2.x);

  // @ts-ignore
  const path = line(points);

  return (
    <path
        className={classNames('trendline', { 'unfocused': props.selectedPoints.size > 0 })}
        d={ path }
      />
  )
};

const ScatterPlot: React.FC<ScatterPlotProps> = (props: ScatterPlotProps) => {
  const points = props.points;

  const xScale = d3.scaleLinear()
    // @ts-ignore
    .domain(props.xExtent)
    .range([MARGINS.left + PADDINGS.left, MARGINS.left + DIMENSIONS.width - PADDINGS.right - 100]);
  const xTicks = xScale.nice().ticks();

  const yScale = d3.scaleLinear()
    // @ts-ignore
    .domain(props.yExtent)
    .range([MARGINS.top + DIMENSIONS.height, MARGINS.top]);

  const yTicks = yScale.nice().ticks();

  const [selectedPoints, setSelectedPoints] = useState<Set<string>>(new Set());

  return (
    <StyledScatterPlot
        viewBox={`0 0 ${DIMENSIONS.width + MARGINS.left + MARGINS.right} ${DIMENSIONS.height + MARGINS.top + MARGINS.bottom}`}
        className={classNames({
          'dark': props.isDark,
          'light': !props.isDark
        })}
        preserveAspectRatio='xMidYMin meet'
      >
      <VerticalAxis
          xScale={ xScale }
          yScale={ yScale }
          ticks={ yTicks }
          dimensions={ DIMENSIONS }
          margins={ MARGINS }
          paddings={ PADDINGS }
          formatTicks={ props.formatVerticalTicks }
        />
      <HorizontalAxis
          xScale={ xScale }
          yScale={ yScale }
          ticks={ xTicks }
          dimensions={ DIMENSIONS }
          margins={ MARGINS }
          paddings={ PADDINGS }
        />
      <Trendline xScale={ xScale } yScale={ yScale } points={ points } selectedPoints={ selectedPoints } />
      <Points
        xScale={ xScale }
        yScale={ yScale }
        points={ points }
        selectedPoints={ selectedPoints }
        setSelectedPoints={ setSelectedPoints }
      />
    </StyledScatterPlot>
  )
};

export default ScatterPlot;
